/**
 * React Query hook for fetching Dosetronic dosing events data
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type DosingEventFilters } from '@/api/dosing';
import { tankParametersQueryKey } from '@/api/parameters';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';
import type {
  ManualDosingRequest,
  ManualDosingUpdateRequest,
  DosingScheduleCreateRequest,
  DosingScheduleUpdateRequest,
} from '@/types/dosing';

interface UseDosingEventsOptions {
  enabled?: boolean;
}

export function useDosingEvents(filters?: DosingEventFilters, options?: UseDosingEventsOptions) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: ['dosing-events', tankId, filters],
    queryFn: () => api.getDosingEvents(tankId!, filters),
    enabled: !!tankId && (options?.enabled ?? true),
    staleTime: CACHE_TIMES.LIVE,
    retry: 2,
  });
}

/**
 * React Query hook for fetching dosing chemicals (for autocomplete)
 */
export function useDosingChemicals() {
  return useQuery({
    queryKey: ['dosing-chemicals'],
    queryFn: () => api.getDosingChemicals(),
    staleTime: CACHE_TIMES.DATA, // chemicals rarely change
  });
}

/**
 * React Query mutation hook for creating manual dosing events
 */
export function useCreateManualDosing() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ManualDosingRequest) =>
      api.createManualDosingEvent(tankId!, data),
    onSuccess: () => {
      // Invalidate dosing queries to refresh the table
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      // Also invalidate dashboard if it shows dosing data
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      // Refresh tank capability flags (has_doser) so the Dosing nav item appears
      queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(tankId) });
    },
  });
}

/**
 * React Query mutation hook for deleting manual dosing events
 */
export function useDeleteManualDosing() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: number) =>
      api.deleteManualDosingEvent(tankId!, eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      // Deleting the last event can flip has_doser off; refresh nav flags
      queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(tankId) });
    },
  });
}

/**
 * React Query mutation hook for toggling dosing event exclusion
 */
export function useToggleDosingExclusion() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, excluded }: { eventId: number; excluded: boolean }) =>
      api.toggleDosingExclusion(tankId!, eventId, excluded),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
      queryClient.invalidateQueries({ queryKey: ['analysis', tankId] });
    },
  });
}

/**
 * React Query mutation hook for updating manual dosing events
 */
export function useUpdateManualDosing() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: number; data: ManualDosingUpdateRequest }) =>
      api.updateManualDosingEvent(tankId!, eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard', tankId] });
    },
  });
}

// ============================================================================
// Dosing Schedule Hooks
// ============================================================================

/**
 * React Query hook for fetching dosing schedules
 */
export function useDosingSchedules(isActive?: boolean) {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: ['dosing-schedules', tankId, isActive],
    queryFn: () => api.getDosingSchedules(tankId!, isActive),
    enabled: !!tankId,
    staleTime: CACHE_TIMES.LIVE,
  });
}

/**
 * React Query mutation hook for creating a dosing schedule
 */
export function useCreateDosingSchedule() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DosingScheduleCreateRequest) =>
      api.createDosingSchedule(tankId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-schedules', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      // Refresh tank capability flags (has_doser) so the Dosing nav item appears
      queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(tankId) });
    },
  });
}

/**
 * React Query mutation hook for updating a dosing schedule
 */
export function useUpdateDosingSchedule() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, data }: { scheduleId: string; data: DosingScheduleUpdateRequest }) =>
      api.updateDosingSchedule(tankId!, scheduleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-schedules', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
    },
  });
}

/**
 * React Query mutation hook for deleting a dosing schedule
 */
export function useDeleteDosingSchedule() {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, deleteEvents }: { scheduleId: string; deleteEvents: boolean }) =>
      api.deleteDosingSchedule(tankId!, scheduleId, deleteEvents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dosing-schedules', tankId] });
      queryClient.invalidateQueries({ queryKey: ['dosing-events', tankId] });
      // Deleting the last schedule can flip has_doser off; refresh nav flags
      queryClient.invalidateQueries({ queryKey: tankParametersQueryKey(tankId) });
    },
  });
}
